// src/com/trans_plan_return/TransPlanReturnNoReturnInput.js
import React, { useEffect, useMemo, useState } from 'react';
// import { styled } from '@mui/material/styles';
import { Button, DialogContent, DialogActions, Grid, CircularProgress } from '@mui/material';
import { AppWord, AppObject, AppStyle, InputType, CodeGroup } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import { ErrorHintBox } from '../../common/VzmStyled';
import { useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { codeTreeRepo } from '../../value/AppModel';
import ValueMisc from '../../value/ValueMisc';
import IsValid from '../../value/IsValid';
import RadioSelector from '../../common/RadioSelector';

const InputMap = {
    planId : {
		id: 'planId', label: '배차번호', required: true, toAdd: true, toEdit: true,
	}, // varchar(12)
    dcNoReturnCode : {
		id: 'dcNoReturnCode', label: '사유',
		type: InputType.radio, required: true, toAdd: true, toEdit: true,
        valueKey: 'codeId', labelKey: 'codeName',
		check: (value) => {return IsValid.numericString(value, 1, 1)},
	}, // varchar(2)
    dcNoReturnNote : {
		id: 'dcNoReturnNote', label: '상세내용',
		type: InputType.text, required: false, toAdd: true, toEdit: true,
		help: '30자 이내로 입력하세요.',
		check: (value) => {return IsValid.string(value, 0, 30)},
	}, // varchar(100)
};

export default function TransPlanReturnNoReturnInput({
    open, item, onClose, onRequestSave, responseSaving
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const codeTree = useReactiveVar(codeTreeRepo); // ValueMisc.getCodes(codeTree, CodeGroup.DC_NO_RETURN_CD);
    const reasonCodes = useMemo(() => {
        if(codeTree) {
            return ValueMisc.getCodes(codeTree, CodeGroup.DC_NO_RETURN_CD);
        }
        return [];
    }, [codeTree])
    const openConfirmDialog = useConfirmDialog();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    useEffect(()=>{
        if(open && item) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = true; // no meaning
            // 사실상 item은 항상 있음. trans_plan_return을 trans_plan 테이블에 left join한 결과이므로.
            inputSet.data = {
                planId: item.planId,
                dcNoReturnCode: item.dcNoReturnCode || '3',
                dcNoReturnNote: item.dcNoReturnNote,
            };
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            title={`반납정보 미입력 사항 수정 (${item.storeName})`}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <RadioSelector
                            dict={InputMap.dcNoReturnCode}
                            value={input.data.dcNoReturnCode}
                            list={reasonCodes}
                            onChange={onChangeValue}
                            disabled={loadingSave}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.dcNoReturnNote}
                            value={input.data.dcNoReturnNote}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}